import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Schema } from '../../../../../amplify/data/resource';
import SelectTransparent from '../../../../components/custom/SelectTransparent';
import TextFieldUnderline from '../../../../components/custom/TextFieldUnderline';

// @ts-ignore
type User = Schema['User']['type'];

interface Props {
  open: boolean;
  onClose: () => void;
  client: User;
}

interface FormValues {
  source: string;
  companyName: string;
  companyRegistrationNumber: string;
  authorizedSignatoryName: string;
  userId: string;
  authorizedSignatoryPhone: string;
  email: string;
  bank: string;
  branch: string;
  accountNumber: string;
}

const apiClient = generateClient<Schema>();

const EditClient: React.FC<Props> = ({ open, onClose, client }) => {
  const theme = useTheme();

  const handleClose = () => {
    onClose();
  };

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      source: client?.source ?? '',
      companyName: client?.companyName ?? '',
      companyRegistrationNumber: client?.companyRegistrationNumber ?? '',
      authorizedSignatoryName: client?.authorizedSignatoryName ?? '',
      userId: client?.userId ?? '',
      authorizedSignatoryPhone: client?.authorizedSignatoryPhone ?? '',
      email: client?.email ?? '',
      bank: client?.paymentInfo?.bank ?? '',
      branch: client?.paymentInfo?.branch ?? '',
      accountNumber: client?.paymentInfo?.account ?? '',
    },
  });

  const onSubmit = async (formData: FormValues) => {
    try {
      const updateData = {
        id: client.id,
        source: formData.source,
        companyName: formData.companyName,
        companyRegistrationNumber: formData.companyRegistrationNumber,
        authorizedSignatoryName: formData.authorizedSignatoryName,
        userId: formData.userId,
        authorizedSignatoryPhone: formData.authorizedSignatoryPhone,
        email: formData.email,
        paymentInfo: {
          bank: formData.bank,
          branch: formData.branch,
          account: formData.accountNumber,
          type: client?.paymentInfo?.type || 'BUSINESS',
        },
      };
      
      await apiClient.models.User.update(updateData);
      console.log('Client updated successfully');
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to submit contact:', error);
    }
  };

  return (
    <Dialog
      className="edit-dialog"
      fullScreen
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#E7ECF1',
          borderRadius: { xs: '0', sm: '16px' },
          padding: { xs: '20px', sm: '40px', md: '60px' },
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: { xs: '100%', md: '350px' },
          height: '100%',
          backgroundImage: 'url(/assets/client/background.svg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'left center',
          opacity: 0.5,
          zIndex: 0,
        }}
      />

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: { xs: '10px', sm: '20px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            width: '50px',
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
          }}
        >
          <img
            alt="close"
            src={'/assets/client/close.svg'}
            width="35"
            height="35"
            style={{ cursor: 'pointer' }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: '24px',
          width: '100%',
          px: { xs: '16px', sm: '32px', md: '60px' },
          pb: { xs: '16px', sm: '32px' },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1200px',
            mx: 'auto',
          }}
        >
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pt: '10px',
              px: '100px',
              backgroundColor: 'transparent',
              flex: 1,
              overflowY: 'auto',
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Stack
              component="form"
              spacing={5}
              justifyContent={'space-between'}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Stack justifyContent="flex-start">
                <Typography
                  fontSize="60px"
                  fontWeight="300"
                  fontFamily={'AlmoniDL'}
                  textAlign="right"
                >
                  עריכת לקוח
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" spacing={5}>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="מייל"
                      fullWidth
                      disabled
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
                <Controller
                  name="source"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <SelectTransparent
                      {...field}
                      id={field.name}
                      label="מקור"
                      error={!!errors.source}
                      helperText={errors.source?.message}
                      options={[
                        { label: 'ישראכרט', value: 'ישראכרט' },
                        { label: 'לאומי', value: 'לאומי' },
                        { label: 'אחר', value: 'אחר' },
                      ]}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={5}>
                <Controller
                  name="bank"
                  control={control}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="שם בנק"
                      fullWidth
                      error={!!errors.bank}
                      helperText={errors.bank?.message}
                    />
                  )}
                />
                <Controller
                  name="companyName"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="שם העסק"
                      fullWidth
                      required
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={5}>
                <Controller
                  name="branch"
                  control={control}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="סניף"
                      fullWidth
                      error={!!errors.branch}
                      helperText={errors.branch?.message}
                    />
                  )}
                />
                <Controller
                  name="companyRegistrationNumber"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="ח.פ."
                      fullWidth
                      required
                      error={!!errors.companyRegistrationNumber}
                      helperText={errors.companyRegistrationNumber?.message}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={5}>
                <Controller
                  name="accountNumber"
                  control={control}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="מספר חשבון"
                      fullWidth
                      error={!!errors.accountNumber}
                      helperText={errors.accountNumber?.message}
                    />
                  )}
                />
                <Controller
                  name="authorizedSignatoryName"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="שם מורשה חתימה"
                      fullWidth
                      required
                      error={!!errors.authorizedSignatoryName}
                      helperText={errors.authorizedSignatoryName?.message}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={5}>
                <Box sx={{ width: '100%' }} />
                <Controller
                  name="userId"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="תעודת זהות"
                      fullWidth
                      required
                      error={!!errors.userId}
                      helperText={errors.userId?.message}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={5}>
                <Button
                  type="submit"
                  sx={{
                    backgroundColor: '#B6F829',
                    borderRadius: '15px',
                    ':hover': { backgroundColor: '#B6F829' },
                    width: '100%',
                  }}
                >
                  <Typography
                    fontSize="32px"
                    fontWeight="700"
                    color="black"
                    fontFamily={'AlmoniDL'}
                  >
                    שמירה
                  </Typography>
                </Button>
                <Controller
                  name="authorizedSignatoryPhone"
                  control={control}
                  rules={{ required: 'חובה למלא את השדה' }}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="טלפון לשליחת ווטסאפ"
                      fullWidth
                      required
                      disabled
                      error={!!errors.authorizedSignatoryPhone}
                      helperText={errors.authorizedSignatoryPhone?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </DialogContent>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditClient;
